import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft } from "@phosphor-icons/react";
import { LegalHeader, LegalFooter, Section } from "@/pages/Impressum";

/**
 * Datenschutzerklärung gemäß EU-Datenschutz-Grundverordnung (DSGVO/GDPR)
 * und österreichischem Datenschutzgesetz (DSG).
 */
export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] relative z-10" data-testid="datenschutz-root">
      <Helmet>
        <title>Datenschutzerklärung · Bodenblick</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <LegalHeader />

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
          data-testid="datenschutz-back"
        >
          <CaretLeft size={14} /> Zurück zur Startseite
        </Link>

        <div>
          <div className="eid-label">Rechtliches</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">
            Datenschutzerklärung
          </h1>
          <p className="text-sm text-zinc-400 mt-2">
            Informationen gemäß Art. 13 &amp; 14 DSGVO (EU 2016/679) sowie dem
            österreichischen Datenschutzgesetz (DSG).
          </p>
        </div>

        <Section title="0. Bodenblick als Info-Plattform — Stand 2026">
          <p>
            Bodenblick wird aktuell als <strong>reine Informations-Plattform</strong>
            ohne User-Konten betrieben. Es ist <strong>keine Registrierung</strong> möglich
            und auch nicht vorgesehen. Daraus folgt:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>Wir verarbeiten <strong>keine</strong> Profil-, Stamm- oder Kontaktdaten von Besucher:innen.</li>
            <li>Es gibt <strong>keine Newsletter-Anmeldung, kein Konto, kein Marktplatz mit User-Inseraten</strong>.</li>
            <li>Externe Datenquellen (Copernicus, Open-Meteo, Nominatim) werden ausschließlich serverseitig abgefragt — deine IP wird <strong>nicht</strong> an diese Dienste weitergegeben.</li>
            <li>Lediglich die Bodenblick-Redaktion (Admin) authentifiziert sich via Google OAuth, um Inhalte zu veröffentlichen.</li>
          </ul>
          <p className="mt-2">
            <strong>Hinweis zur Server-Infrastruktur:</strong> Die Plattform läuft auf
            der Hosting-Infrastruktur von <em>Emergent Labs (USA)</em>. Server-Logs
            (mit IP-Adressen) werden dort kurzzeitig zur Betriebssicherheit gespeichert.
            Es liegt aktuell <strong>keine EU-Auftragsverarbeitungsvereinbarung (DPA)</strong>
            vor — die Verarbeitung deiner IP außerhalb der EU stützt sich auf
            Art. 49 Abs. 1 lit. d DSGVO (notwendig für die Erbringung der Information).
            Wir minimieren das so weit wie technisch möglich.
          </p>
        </Section>

        <Section title="1. Verantwortliche Stelle">
          <p>
            Verantwortlich für die Verarbeitung personenbezogener Daten auf dieser
            Website ist:
          </p>
          <p>
            <strong>Johanna Maislinger</strong>
            <br />
            Steinbruch 5, 4822 Bad Ischl, Österreich
            <br />
            E-Mail:{" "}
            <a href="mailto:vision@bodenblick.com" className="underline">
              vision@bodenblick.com
            </a>
          </p>
        </Section>

        <Section title={`2. Grundsatz „Privacy by Design"`}>
          <p>
            Bodenblick ist so konzipiert, dass möglichst wenige personenbezogene
            Daten verarbeitet werden. Externe Datenquellen (Copernicus CDSE,
            Open-Meteo, yfinance, Emergent LLM-Gateway) werden ausschließlich{" "}
            <strong>serverseitig</strong> angesprochen. Die Browser unserer
            Nutzer:innen kommunizieren nicht direkt mit diesen Drittanbietern.
            Dabei werden Metadaten wie IP-Adresse, User-Agent oder Cookies
            <em> nicht</em> an die externen Dienste weitergegeben.
          </p>
        </Section>

        <Section title="3. Welche Daten wir verarbeiten">
          <p className="font-medium">a) Newsletter / Warteliste (Double-Opt-In)</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              E-Mail-Adresse (Pflichtangabe), Zeitpunkt der Anmeldung und der
              Bestätigung, Status (pending/confirmed/unsubscribed)
            </li>
            <li>Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</li>
            <li>
              Zweck: Versand unseres wöchentlichen Bodenblick-Berichts sowie
              transaktionaler Bestätigungs-Mails
            </li>
            <li>
              Speicherdauer: bis zum Widerruf der Einwilligung. Abbestellen ist
              jederzeit über den Link am Ende jeder Mail oder per Nachricht an
              vision@bodenblick.com möglich.
            </li>
          </ul>

          <p className="font-medium mt-3">b) Registrierte Nutzer:innen (Cockpit / Admin)</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Google-Konto-Daten via Emergent-verwalteter OAuth-Anmeldung:
              E-Mail, Name, Profilbild-URL, Google-Subject-ID
            </li>
            <li>
              Technische Session-Daten: Session-Token (HttpOnly-Cookie),
              Zeitpunkt der Anmeldung, Ablaufdatum
            </li>
            <li>
              Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung
              bzw. vorvertragliche Maßnahmen)
            </li>
            <li>
              Zweck: Authentifizierung, Zugriffs­kontrolle, Bereitstellung des
              Cockpits
            </li>
            <li>
              Speicherdauer: solange das Nutzer­konto aktiv ist. Auf Anfrage
              wird das Konto samt zugehöriger Daten vollständig gelöscht.
            </li>
          </ul>

          <p className="font-medium mt-3">c) Server-Logs</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Beim Aufruf werden durch die Hosting-Infrastruktur kurzfristig
              technische Daten (gekürzte IP, HTTP-Status, Zeitpunkt) zum Schutz
              des Betriebs gespeichert.
            </li>
            <li>
              Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
              an Sicherheit und Stabilität)
            </li>
            <li>Speicherdauer: maximal 14 Tage, danach automatische Löschung.</li>
          </ul>
        </Section>

        <Section title="4. Cookies &amp; lokaler Speicher">
          <p>
            Wir verwenden ausschließlich <strong>technisch notwendige Cookies</strong>
            und lokalen Browser­speicher. Es werden <em>keine</em> Tracking-,
            Werbe- oder Analyse-Cookies gesetzt.
          </p>
          <table className="w-full text-xs mt-2 border-collapse">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left p-2 border border-white/10">Name</th>
                <th className="text-left p-2 border border-white/10">Zweck</th>
                <th className="text-left p-2 border border-white/10">Dauer</th>
                <th className="text-left p-2 border border-white/10">Kategorie</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border border-white/10 font-mono">session_token</td>
                <td className="p-2 border border-white/10">
                  Authentifizierung (HttpOnly, Secure)
                </td>
                <td className="p-2 border border-white/10">7 Tage</td>
                <td className="p-2 border border-white/10">Essentiell</td>
              </tr>
              <tr>
                <td className="p-2 border border-white/10 font-mono">bb.consent</td>
                <td className="p-2 border border-white/10">
                  Speichert deine Cookie-Entscheidung (LocalStorage)
                </td>
                <td className="p-2 border border-white/10">12 Monate</td>
                <td className="p-2 border border-white/10">Essentiell</td>
              </tr>
            </tbody>
          </table>
          <p>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO bzw. § 165 Abs. 3 TKG 2021
            (technisch unbedingt erforderlich). Eine Einwilligung ist für
            technisch notwendige Cookies nicht erforderlich; du kannst sie über
            die Einstellungen deines Browsers jederzeit löschen.
          </p>
        </Section>

        <Section title="5. Empfänger &amp; Auftragsverarbeiter">
          <p>
            Zur Erfüllung unserer Leistung setzen wir sorgfältig ausgewählte
            Dienstleister ein. Mit jedem wurde ein Auftragsverarbeitungs­vertrag
            (AVV) gem. Art. 28 DSGVO abgeschlossen bzw. berufen sich die Dienste
            auf EU-Standard­vertrags­klauseln (SCC) für Drittland­übermittlungen.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Emergent Agent, Inc.</strong> (Hosting &amp; Google-OAuth-Proxy,
              USA) — Standort der Anwendung, Session-Management.
              Rechtsgrundlage: SCC + Art. 46 DSGVO.
            </li>
            <li>
              <strong>Resend, Inc.</strong> (Transaktionale E-Mails, USA) —
              Versand von Bestätigungs- und Newsletter-Mails. SCC + Art. 46 DSGVO.
            </li>
            <li>
              <strong>MongoDB-Hosting</strong> (EU-Region) — Speicherung der
              Anwendungs­daten.
            </li>
            <li>
              <strong>Google LLC / Google Ireland Ltd.</strong> — ausschließlich
              als OAuth-Identitäts­provider, sofern du dich mit Google anmeldest.
              Die Authentifizierung läuft über den Emergent-Proxy; wir erhalten
              nur die freigegebenen Profildaten (Name, E-Mail, Bild-URL).
            </li>
          </ul>
          <p>
            Reine <strong>Datenquellen</strong> (Copernicus CDSE, Open-Meteo,
            yfinance) werden ausschließlich vom Server aufgerufen und verarbeiten
            <em> keine</em> personenbezogenen Daten unserer Nutzer:innen. Sie sind
            daher <em>keine</em> Auftrags­verarbeiter im Sinne der DSGVO.
          </p>
        </Section>

        <Section title="6. Einsatz künstlicher Intelligenz (LLM)">
          <p>
            Für die Erstellung redaktioneller Zusammenfassungen und Social-Media-
            Entwürfe nutzen wir Large-Language-Modelle (Google Gemini) über das
            Emergent-Gateway. Übermittelt werden ausschließlich <strong>anonymisierte
            Kennzahlen</strong> (z. B. NDVI, Bodenfeuchte, Preise) sowie generische
            Regions­labels („Feld A"). Personenbezogene Daten gelangen nicht in die
            Prompts. Einzelne Anfragen werden von den Anbietern gemäß ihrer
            Richtlinien zur Modell­verbesserung kurzfristig gecacht und
            anschließend gelöscht (Retention &lt; 30 Tage).
          </p>
        </Section>

        <Section title="7. Keine automatisierten Entscheidungen">
          <p>
            Es findet keine automatisierte Entscheidungs­findung im Sinne des
            Art. 22 DSGVO statt, die dir gegenüber rechtliche Wirkung entfaltet
            oder dich in ähnlicher Weise erheblich beeinträchtigt. Die auf der
            Website dargestellten Empfehlungen sind <em>Informations­hinweise</em>
            und ersetzen keine fachliche Beurteilung.
          </p>
        </Section>

        <Section title="8. Deine Rechte">
          <p>
            Du hast nach der DSGVO folgende Rechte uns gegenüber:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Recht auf Auskunft (Art. 15)</li>
            <li>Recht auf Berichtigung (Art. 16)</li>
            <li>Recht auf Löschung / „Vergessenwerden" (Art. 17)</li>
            <li>Recht auf Einschränkung der Verarbeitung (Art. 18)</li>
            <li>Recht auf Datenübertragbarkeit (Art. 20)</li>
            <li>Widerspruchsrecht (Art. 21)</li>
            <li>
              Recht, eine erteilte Einwilligung jederzeit zu widerrufen, ohne
              dass die Rechtmäßigkeit der bis dahin erfolgten Verarbeitung berührt
              wird (Art. 7 Abs. 3)
            </li>
          </ul>
          <p>
            Zur Ausübung deiner Rechte genügt eine formlose Nachricht an{" "}
            <a href="mailto:vision@bodenblick.com" className="underline">
              vision@bodenblick.com
            </a>
            . Wir antworten innerhalb eines Monats.
          </p>
        </Section>

        <Section title="9. Beschwerderecht bei der Aufsichtsbehörde">
          <p>
            Du hast das Recht, dich bei einer Datenschutz-Aufsichtsbehörde zu
            beschweren. In Österreich:
          </p>
          <p>
            <strong>Österreichische Datenschutzbehörde (DSB)</strong>
            <br />
            Barichgasse 40–42, 1030 Wien
            <br />
            E-Mail:{" "}
            <a href="mailto:dsb@dsb.gv.at" className="underline">
              dsb@dsb.gv.at
            </a>
            <br />
            Web:{" "}
            <a
              href="https://www.dsb.gv.at"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              www.dsb.gv.at
            </a>
          </p>
        </Section>

        <Section title="10. Datensicherheit">
          <p>
            Sämtliche Daten­übertragungen erfolgen ausschließlich via TLS 1.2+
            (HTTPS). Session-Tokens werden als HttpOnly- und Secure-Cookies
            ausgeliefert. Passwörter werden nicht gespeichert, weil die
            Authentifizierung vollständig über den OAuth-Provider abläuft.
          </p>
        </Section>

        <Section title="11. Änderungen dieser Erklärung">
          <p>
            Wir behalten uns vor, diese Datenschutz­erklärung anzupassen, wenn
            sich die Rechtslage oder unsere Verarbeitungs­vorgänge ändern. Die
            jeweils aktuelle Fassung ist unter{" "}
            <a href="/datenschutz" className="underline">
              bodenblick.com/datenschutz
            </a>{" "}
            abrufbar.
          </p>
        </Section>

        <div className="pt-4 border-t border-white/10 text-xs text-zinc-500 font-mono">
          Stand: {new Date().toLocaleDateString("de-AT", { year: "numeric", month: "long" })}
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
